import md5 from 'md5'
import {
  _ONPREM,
  ADMIN_CLUSTER_URL_NAME,
  FAWKES_APP_IDS,
  MGMT_MANAGE_ROUTE,
  MGMT_OVERVIEW_ROUTE,
  ONPREM,
  PAAS_REDIRECT_URL_NAME,
  TSG_ID_NONE,
} from '../../framework/constants'
import { getDatadogRum } from '../../framework/externals'
import history from '../../framework/history'
import { jwtDecode as jwt_decode } from '../../framework/jwt'
import { getFrameworkServiceUrl } from '../../framework/states'
import { stateRef } from '../../framework/store'
import {
  clearCurrentTsgId,
  getCurrentTsgId,
  getTsgToken,
  getUserManager,
} from '../../framework/tsg'
import { dedupPromise, noop, tryParseErrorJson, setTimeout, clearTimeout, isNonemptyObject } from '../../framework/utils'
import { isNavItemAvail } from '../../framework/extensions/utils'
import { __error, __info, __warn } from '../../init/debug'
import fetch from '../../libs/fetch'
import { globalAvail, tsgAccessAvail } from '../common/utils'
import { _timeouts } from '../../init/performance'

import type { FawkesAvailFunc, NavItem } from '../../framework/microAppTypes'
import type { AnyState, AuthState, ConfigLocation, FeatureInfo, TokenInfo } from '../../framework/stateTypes'
import type { TsgInstances } from '../../framework/indexedArrays'

export * from '../../framework/loadingMask'
export { jwt_decode }

type FetchPromiseOption = {
  signal?: AbortController['signal']
  tsg_id?: string
  baseUrl?: string
  jwt?: string
}

// const JWT_IAT_TOLERANCE = 60000 // 60s

export const getAdminClusterURL = (): string | undefined => getFrameworkServiceUrl(ADMIN_CLUSTER_URL_NAME)

export const getLoginServiceURL = (serviceUrl: string) => ({
  SERVICE_URL: `${getAdminClusterURL()}/${serviceUrl}`
})

export const clearUserSessionForLogout = () => {
  if (getCurrentTsgId() === TSG_ID_NONE) {
    clearCurrentTsgId()
    return
  }
  __info('clear fawkes session info and clear session storage')
  const sub = sessionStorage.getItem('currentTenantId') || localStorage.getItem('currentTenantId')
  if (sub) {
    localStorage.removeItem(`token-${sub}`)
    localStorage.removeItem(`token-exp-${sub}`)
    localStorage.removeItem('currentTenantId')
  }
  clearCurrentTsgId()
  sessionStorage.clear()
}

export const isTokenValid = ({ exp, iat }: { exp: number, iat: number }) => {
  const now = Date.now()
  return exp && iat &&
    now < exp * 1000
  // now + JWT_IAT_TOLERANCE > iat * 1000 // allow 60s local time diff
}

/**
 * @deprecated
 */
export const doLogin = () => {
  console.error('non-tsg fawkes doLogin is no longer supported')
}

/**
 * @deprecated
 */
export const doLogout = () => {
  console.error('non-tsg fawkes doLogout is no longer supported')
}

export function getCSRFToken() {
  const now = Date.now()
  return `${now}:${md5(`${now}:`)}`
}

export function getMd5Salt() {
  return ''
}

export function getAuthHeaders(): Record<string, string> {
  const access_token = stateRef.current.auth?.access_token
  return {
    // 'x-csrf-token': getCSRFToken(), // no longer needed
    'x-auth-jwt': access_token || '',
  }
}

export const isFawkesAppId = (appId: string) => FAWKES_APP_IDS.includes(appId)
export const getFawkesInst = (instances: TsgInstances | undefined) => {
  if (instances?.length) {
    for (const appId of FAWKES_APP_IDS) {
      const inst = instances.get(appId)
      if (inst) { // already handled the hidden case when indexing
        return inst
      }
    }
  }
  return undefined
}
export const hasRunningFawkesInst = (instances: TsgInstances | undefined) => {
  if (instances?.isFawkesRunning) {
    return true
  }
  if (instances?.length) {
    return FAWKES_APP_IDS.some(appId => instances.isRunningStatus(appId))
  }
  return undefined
}
export const isPanoramaManagedTenant = (auth: AuthState = stateRef.current.auth): boolean => {
  const fawkesInst = getFawkesInst(auth.instances)
  if (!fawkesInst) {
    return false
  }
  if (fawkesInst.extra?.platform) {
    return fawkesInst.extra?.platform === ONPREM
  }
  return fawkesInst.tenant_id.endsWith(_ONPREM)
}

export const isMigrationStateEligibleForFawkesUI = (): boolean => {
  const status = stateRef.current.main?.migrationStatus
  const hasStatus = [
    'LOAD_CFG_DONE',
    'LICENSE_TRANSFER_DONE',
    'LICENSE_TRANSFER_FAIL',
    'MIGRATION_FAILED',
    'COMMIT_CFG_FAILED'
  ].includes(status)

  return hasStatus
}

export const getParam = (arg: string) => {
  const params = new URLSearchParams(window.location.search)
  if (params.has(arg)) {
    return params.get(arg) || undefined
  }
  return undefined
}

export const updateSession = ({ sub, token, exp }: { sub: string, token: string, exp: number }) => {
  if (token && token !== 'null') {
    localStorage.setItem(`token-${sub}`, token)
  }
  else {
    localStorage.deleteItem(`token-${sub}`)
  }
  if (exp) {
    const expStr = String(exp)
    localStorage.setItem(`token-exp-${sub}`, expStr)
    sessionStorage.setItem(`token-exp-${sub}`, expStr)
  }
  else {
    localStorage.deleteItem(`token-exp-${sub}`)
    sessionStorage.deleteItem(`token-exp-${sub}`)
  }
}

export const saveSession = ({ sub, token, exp }: { sub: string, token: string, exp: number }) => {
  sessionStorage.setItem('currentTenantId', sub)
  localStorage.setItem('currentTenantId', sub)
  updateSession({ sub, token, exp })
  startSessionMonitor()
}

export const unsetSession = () => { // does not really clear the jwt
  sessionStorage.removeItem('currentTenantId')
  localStorage.removeItem('currentTenantId')
}

export const getSession = (sub?: string): string | undefined => {
  const { tsg_id, fawkes_tenant_id, access_token, access_token_decoded } = stateRef.current.auth || {}
  if (access_token && (!sub || sub === fawkes_tenant_id)) {
    if (access_token_decoded?.exp && access_token_decoded.exp * 1000 > Date.now()) {
      return access_token
    }
    console.error('access access_token from auth and its expired', access_token_decoded?.exp)
    getTsgToken(tsg_id).catch(noop) // try renew
    return undefined
  }
  const tenantId = sub || sessionStorage.getItem('currentTenantId')
  const token = tenantId && localStorage.getItem(`token-${tenantId}`)
  return (token !== 'null' && token) || undefined
}

let sessionTimer: number | undefined
export const startSessionMonitor = () => {
  if (sessionTimer) {
    clearTimeout(sessionTimer)
  }
  const INTERVAL = 10000 // 10s
  const setTimer = () => {
    sessionTimer = setTimeout(() => {
      const { access_token_decoded } = stateRef.current.auth || {}
      if (access_token_decoded?.tsg_id && +access_token_decoded.exp && access_token_decoded.exp * 1000 < Date.now() + INTERVAL) {
        const userMgr = getUserManager(access_token_decoded.tsg_id)
        if (userMgr?.settings.automaticSilentRenew) {
          __warn('found user session expiring soon without successful renew, try renewing it')
          userMgr.signinSilent().then(setTimer, (err) => {
            __error('failed to renew user session, will retry again after expired', err)
            setTimeout(() => {
              getTsgToken(access_token_decoded.tsg_id).then(setTimer, (err) => {
                __error('failed to renew user session after retry', err)
                // setTimer()
              })
            }, INTERVAL)
          })
        }
        return
      }
      setTimer()
    }, INTERVAL)
  }
  setTimer()
}

export function getFawkesTenantId(): string | undefined {
  const { auth } = stateRef.current
  const tenantId = auth?.fawkes_tenant_id ||
    sessionStorage.getItem('currentTenantId')
  return (tenantId !== 'null' && tenantId) || undefined
}

export function getRedirectURL() {
  const redirectUrl = getFrameworkServiceUrl(PAAS_REDIRECT_URL_NAME)
  if (redirectUrl) {
    return redirectUrl
  }
  console.error('failed to get redirect url from state')
  return ''
}

export function getAdminServiceURL(adminUrl: string, extraParams?: Record<string, any>) {
  let urlParams = '?'
  if (isNonemptyObject(extraParams)) {
    for (const key in extraParams) {
      if (key && extraParams[key]) {
        urlParams = `${urlParams}&${key}=${extraParams[key]}`
      }
    }
  }
  urlParams = urlParams === '?' ? '' : urlParams
  return {
    LOGIN_SERVICE_URL: `${getAdminClusterURL()}/${adminUrl}${urlParams}`
  }
}

export const checkIsFeatureEnabled = (featureList: FeatureInfo[] = [], featureKey = '') => {
  return featureList.some(feature => feature?.name === featureKey && feature.toggle === 1)
}

export const isValidRoute = (route: NavItem) => {
  // has a copy in RouteViewer, ensure in sync
  const { auth, main } = stateRef.current
  const { licenseInfo, featuresInfo } = main || {}
  const { instances, access, tsg_id } = auth || {}
  const configLocation = { container: {} } as ConfigLocation // for backward compatibility
  const objs = {
    ...configLocation,
    configLocation,
    location: history.location,
    licenseInfo,
    featuresInfo,
    instances,
    tsg_id,
    access,
    auth,
    main,
    navItem: route,
  }
  try {
    const isAvail = !route.avail || Boolean((route.avail as FawkesAvailFunc)(objs, licenseInfo, featuresInfo))
    return isAvail && isNavItemAvail(route) &&
      Boolean(route.bypassGlobalAvail || globalAvail()) &&
      Boolean(route.bypassTsgAvail || tsgAccessAvail())
  }
  catch (e) {
    console.error('failed to validate route due to exception', route, e)
    return false
  }
}

export const isInManageRoute = (path: string) =>
  path.startsWith(MGMT_OVERVIEW_ROUTE) || path.startsWith(MGMT_MANAGE_ROUTE)

export function sendErrorToDatadog(err: Error): never {
  try {
    getDatadogRum()?.addError(err, {
      type: 'server_error',
    })
  }
  catch (_) {
    // ignore
  }
  throw err
}

export function fetchLoggedInUser(tsg_id: string | undefined) {
  const url = new URL('api/sso/v1/user', getRedirectURL())
  return dedupPromise(`${url}?tsgId=${tsg_id}`, () => fetch(url.toString(), {
    credentials: 'omit',
    mode: 'cors',
    method: 'GET',
    timeout: _timeouts.fawkesApiFetchTimeout,
    headers: getAuthHeaders()
  }).then(async (resp) => {
    if (resp.ok) {
      const userInfo = await resp.json()
      return userInfo
    }
    const data = await resp.text().then(tryParseErrorJson)
    const err = new Error(`${resp.status}: failed to fetch user and tenant info from fawkes`)
    Object.defineProperty(err, 'cause', { value: data })
    sendErrorToDatadog(err)
  }, sendErrorToDatadog), { keepPromiseIn: 30000 }) // keep 30s
}

export function fetchFawkesMainInfo<T = AnyState>({ signal, url, tsg_id, jwt, errorMsg }: { url: string, tsg_id?: string, jwt?: string, signal?: AbortController['signal'], errorMsg: string }): Promise<T> {
  return dedupPromise(tsg_id ? `${url}?tsgId=${tsg_id}` : url, () => fetch(url, {
    credentials: 'omit',
    mode: 'cors',
    method: 'GET',
    signal,
    timeout: _timeouts.fawkesApiFetchTimeout,
    headers: jwt ? { 'x-auth-jwt': jwt } : getAuthHeaders()
  }).then(async (resp) => {
    if (resp.ok) {
      return resp.json() as Promise<T>
    }
    const data = await resp.text().then(tryParseErrorJson)
    const err = new Error(`${resp.status}: ${errorMsg}`)
    Object.defineProperty(err, 'cause', { value: data })
    sendErrorToDatadog(err)
  }, sendErrorToDatadog), { keepPromiseIn: 30000 }) // keep 30s
}

export function fetchTokens() {
  const url = new URL('api/ui/tokens', getAdminClusterURL()).toString()
  return fetchFawkesMainInfo<TokenInfo>({ url, errorMsg: 'failed to fetch tokens from fawkes' })
}

export function fetchFeatures({ signal, tsg_id, baseUrl, jwt }: FetchPromiseOption) {
  const url = new URL('api/features', baseUrl || getAdminClusterURL()).toString()
  return fetchFawkesMainInfo<FeatureInfo[]>({ url, tsg_id, jwt, signal, errorMsg: 'failed to fetch features from fawkes' })
}

export function fetchFqdnInfo({ signal, tsg_id, baseUrl, jwt }: FetchPromiseOption) {
  const url = new URL('api/extensions/gpcs/gpFQDN', baseUrl || getRedirectURL()).toString()
  return fetchFawkesMainInfo({ url, tsg_id, jwt, signal, errorMsg: 'failed to fetch FQDNs from fawkes' })
}

export function fetchLicenseInfo({ signal, tsg_id }: FetchPromiseOption) {
  const url = new URL('api/extensions/gpcs/license', getRedirectURL()).toString()
  return fetchFawkesMainInfo({ url, tsg_id, signal, errorMsg: 'failed to fetch license info from fawkes' })
}

export function getNotificationsPromise({ signal, tsg_id }: FetchPromiseOption) {
  const url = new URL('api/v1/notifications?readStatus=false', getAdminClusterURL()).toString()
  return fetchFawkesMainInfo({ url, tsg_id, signal, errorMsg: 'failed to notifications from fawkes' })
}

export function getMigrationSatusPromise({ signal, tsg_id }: FetchPromiseOption) {
  const url = new URL('api/migration/status', getRedirectURL()).toString()
  return fetchFawkesMainInfo({ url, tsg_id, signal, errorMsg: 'failed to migration status from fawkes' })
}

export function getSubTenantInfoPromise({ signal, tsg_id }: FetchPromiseOption) {
  const url = new URL('api/extensions/gpcs/get/get_tenant_subtenant_info', getRedirectURL()).toString()
  return fetchFawkesMainInfo({ url, tsg_id, signal, errorMsg: 'failed to sub tenant info from fawkes' })
}
